"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getConsumedLoanTapeMetadataProperties = exports.extractLoanTapeMetadata = exports.parseLoanTapeMetadataProperties = exports.isCustomerProvidedValuesLoanTapeMetadataVariable = exports.isPortfolioCovenantLoanTapeMetadata = exports.parseEncodedPortfolioCovenantLoanTapeMetadata = exports.isStaticLoanTapeMetaDataIterable = exports.isLoanTapeMetaDataIterable = exports.parsePrimitiveLoanTapeMetadataVariables = exports.generateLoanTapeMetadataRegex = void 0;
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const exampleEncodedPortCov = `loanTapeMetadata.portfolioCovenants__key.<portCovKey>_period.<period>_responseProperty.<responseProperty>`;
const generateLoanTapeMetadataRegex = (prop) => new RegExp(`\\bloanTapeMetadata\\.${prop}(?![A-Za-z0-9_])`, 'g');
exports.generateLoanTapeMetadataRegex = generateLoanTapeMetadataRegex;
const parsePrimitiveLoanTapeMetadataVariables = (str) => {
    const variables = [];
    finley_types_1.Receivable.CreditFacility.LoanTapeMetadataPrimitiveProperties.forEach((prop) => {
        // Ensure exact match: look for `loanTapeMetadata.<prop>` followed by
        // either a non-word char (like space, }) or end-of-string
        const regex = (0, exports.generateLoanTapeMetadataRegex)(prop);
        if (regex.test(str)) {
            variables.push(prop);
        }
    });
    return variables;
};
exports.parsePrimitiveLoanTapeMetadataVariables = parsePrimitiveLoanTapeMetadataVariables;
const isLoanTapeMetaDataIterable = (str) => finley_types_1.Receivable.CreditFacility.LoanTapeMetadataIterable.includes(str);
exports.isLoanTapeMetaDataIterable = isLoanTapeMetaDataIterable;
const isStaticLoanTapeMetaDataIterable = (str) => finley_types_1.Receivable.CreditFacility.StaticLoanTapeMetadataProperties.includes(str);
exports.isStaticLoanTapeMetaDataIterable = isStaticLoanTapeMetaDataIterable;
const parseEncodedPortfolioCovenantLoanTapeMetadata = (variable) => {
    // loanTapeMetadata.portfolioCovenants__key1.value1_key2.value2
    const [loanTapeMetadata, ...rest] = variable.split('.');
    const encodedPortCov = rest.join('.'); // recombine encodedPortCov into single string
    (0, common_1.finleyAssert)(loanTapeMetadata === 'loanTapeMetadata', new common_1.error.FinleyError(`parsePortfolioCovenantLoanTapeMetadata variable must be of the form ${exampleEncodedPortCov} but got [variable:${variable}]`));
    // portfolioCovenants__key1.value1_key2.value2
    const [portfolioCovenants, queryParams] = encodedPortCov.split(finley_types_1.PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_START_ANCHOR);
    (0, common_1.finleyAssert)(portfolioCovenants === 'portfolioCovenants', new common_1.error.FinleyError(`parsePortfolioCovenantLoanTapeMetadata encoded portfolioCovenant must be of the form ${exampleEncodedPortCov} but got [variable:${variable}]`));
    const params = queryParams.split(finley_types_1.PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_DELIMITER);
    // we need dynamically construct PortfolioCovenantLoanTapeMetadataParams
    // from the encoded params. since we are constructing dynamically, we need to be
    // loose on the initial construction, and will typeguard the resultant shape.
    const partialPortfolioCovenantLoanTapeMetadataParams = params.reduce((acc, param) => {
        const [property, val] = param.split(finley_types_1.PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_KEY_VALUE_SEPARATOR);
        (0, common_1.finleyAssert)(finley_types_1.typeguards.portfolioCovenant.isAllPortfolioCovenantLoanTapeMetadataParams(property), `Expected property to be a valid PortfolioCovenantLoanTapeMeta property, but got [property:${property}]`);
        (0, common_1.finleyAssert)(typeof val === 'string', `Expected PortfolioCovenantLoanTapeMeta value to be type string, [type:${typeof val}] for [val:${val}] of [property:${property}]`);
        switch (property) {
            // root level props
            case 'key': {
                // eslint-disable-next-line no-param-reassign
                acc[property] = val;
                break;
            }
            case 'responseProperty':
                // eslint-disable-next-line no-param-reassign
                acc[property] = val;
                break;
            // nested ReportingPeriod props
            case 'period':
                if (!acc.reportingPeriod) {
                    // eslint-disable-next-line no-param-reassign
                    acc.reportingPeriod = {};
                }
                // eslint-disable-next-line no-param-reassign
                acc.reportingPeriod[property] = val;
                break;
            case 'weekStartDay':
                if (!acc.reportingPeriod) {
                    // eslint-disable-next-line no-param-reassign
                    acc.reportingPeriod = {};
                }
                // eslint-disable-next-line no-param-reassign
                acc.reportingPeriod[property] = val;
                break;
            default:
                (0, common_1.finleyAssertUnreachable)(property);
        }
        return acc;
    }, {});
    const portfolioCovenantLoanTapeMetadataParams = {
        ...partialPortfolioCovenantLoanTapeMetadataParams,
        queryString: encodedPortCov,
    };
    (0, common_1.finleyAssert)(finley_types_1.typeguards.portfolioCovenant.isPortfolioCovenantLoanTapeMetadataParams(portfolioCovenantLoanTapeMetadataParams), new common_1.error.FinleyError(`isPortfolioCovenantLoanTapeMetadataParams typeguard failure`, {
        metadata: {
            logMeta: {
                portfolioCovenantLoanTapeMetadataParams: JSON.stringify(portfolioCovenantLoanTapeMetadataParams),
            },
        },
    }));
    return portfolioCovenantLoanTapeMetadataParams;
};
exports.parseEncodedPortfolioCovenantLoanTapeMetadata = parseEncodedPortfolioCovenantLoanTapeMetadata;
const isPortfolioCovenantLoanTapeMetadata = (str) => {
    // loanTapeMetadata.portfolioCovenants__key1.value1_key2.value2
    const [loanTapeMetadata, ...rest] = str.split('.');
    // reconstruct embeded portCov
    const encodedPortCov = rest.join('.');
    if (loanTapeMetadata !== 'loanTapeMetadata') {
        return false;
    }
    const [portfolioCovenants] = encodedPortCov.split(finley_types_1.PortfolioCovenant.PORTFOLIO_COVENANT_LOAN_TAPE_METADATA_PARAMS_START_ANCHOR);
    // at this point it's safe to say the author is attempting to utilizing
    // loanTapeMetadata.portfolioCovenants loan tape metadata
    return portfolioCovenants === 'portfolioCovenants';
};
exports.isPortfolioCovenantLoanTapeMetadata = isPortfolioCovenantLoanTapeMetadata;
const isCustomerProvidedValuesLoanTapeMetadataVariable = (str) => {
    // loanTapeMetadata.customerProvidedValues.someVariable
    const [loanTapeMetadata, maybeCustomerProvidedValues, ..._rest] = str.split('.');
    const isCustomerProvidedValue = loanTapeMetadata === 'loanTapeMetadata' &&
        maybeCustomerProvidedValues ===
            finley_types_1.CustomerProvidedValues.CUSTOMER_PROVIDED_VALUES;
    return isCustomerProvidedValue;
};
exports.isCustomerProvidedValuesLoanTapeMetadataVariable = isCustomerProvidedValuesLoanTapeMetadataVariable;
const parseLoanTapeMetadataProperties = (formula) => {
    const { variables } = (0, formula_engine_1.inspectDependencies)(formula);
    const portfolioCovenantQueryParams = [];
    const matches = Array.from(variables).map((variable) => {
        // handle portfolio covenant first
        if ((0, exports.isPortfolioCovenantLoanTapeMetadata)(variable)) {
            // parse the variable for query params
            const portCovQueryParams = (0, exports.parseEncodedPortfolioCovenantLoanTapeMetadata)(variable);
            portfolioCovenantQueryParams.push(portCovQueryParams);
            return 'portfolioCovenants';
        }
        if ((0, exports.isCustomerProvidedValuesLoanTapeMetadataVariable)(variable)) {
            return finley_types_1.CustomerProvidedValues.CUSTOMER_PROVIDED_VALUES;
        }
        const [loanTapeMetadata, key] = variable.split('.');
        if (loanTapeMetadata === 'loanTapeMetadata') {
            if ((0, exports.isStaticLoanTapeMetaDataIterable)(key)) {
                return key;
            }
        }
        return null;
    });
    return {
        loanTapeMetadataProperties: (0, remeda_1.compact)(matches),
        portfolioCovenantQueryParams,
    };
};
exports.parseLoanTapeMetadataProperties = parseLoanTapeMetadataProperties;
/**
 * This serves to map GQL type to Calc Agent LoanTapeMetadata.  This seems
 * a little overkill / there should be a simpler way to do this,
 * but it's usage is fairly limited, so won't address at the moment
 */
const extractLoanTapeMetadata = (fields) => {
    return Object.entries(fields).reduce((acc, [key, val]) => {
        if (!(0, exports.isLoanTapeMetaDataIterable)(key) ||
            val === undefined ||
            val === null) {
            return acc;
        }
        if (key === 'reportingDate') {
            if (common_1.typeguards.isFullDateStr(val)) {
                // eslint-disable-next-line no-param-reassign
                acc[key] = val;
            }
        }
        else if (key === 'lastApprovedOrSubmittedFundingRequestDate') {
            if (common_1.typeguards.isFullDateStr(val)) {
                // eslint-disable-next-line no-param-reassign
                acc[key] = val;
            }
        }
        else if (key === 'configuredActiveWaivers') {
            (0, common_1.finleyAssert)(finley_types_1.typeguards.borrowingBase.isReceivableWaiverTestKeyStringsArray(val), new common_1.error.FinleyError('isReceivableWaiverTestKeyStringsArray failed typeguard', {
                metadata: {
                    logMeta: {
                        isReceivableWaiverTestKeyStringsArray: JSON.stringify(val),
                    },
                },
            }));
            // eslint-disable-next-line no-param-reassign
            acc[key] = val;
            // }
        }
        else if (key === 'portfolioCovenants') {
            (0, common_1.finleyAssert)(finley_types_1.typeguards.portfolioCovenant.isPortfolioCovenantLoanTapeMetadataArray(val), new common_1.error.FinleyError('isPortfolioCovenantLoanTapeMetadataArray failed typeguard', {
                metadata: {
                    logMeta: {
                        portfolioCovenantLoanTapeMetadataArray: JSON.stringify(val),
                    },
                },
            }));
            // eslint-disable-next-line no-param-reassign
            acc[key] = val;
        }
        else if (key === finley_types_1.CustomerProvidedValues.CUSTOMER_PROVIDED_VALUES) {
            const parsed = finley_types_1.CustomerProvidedValues.CustomerProvidedLoanTapeMetadataArraySchema.parse(val);
            // eslint-disable-next-line no-param-reassign
            acc[finley_types_1.CustomerProvidedValues.CUSTOMER_PROVIDED_VALUES] = finley_types_1.CustomerProvidedValues.fromGQL(parsed);
        }
        else if (typeof val === 'number') {
            // eslint-disable-next-line no-param-reassign
            acc[key] = val;
        }
        return acc;
    }, {});
};
exports.extractLoanTapeMetadata = extractLoanTapeMetadata;
const getConsumedLoanTapeMetadataProperties = (config) => {
    const { _config_: { intermediateCalcConfigs }, } = config;
    const consumedProperties = new Set();
    const portfolioCovenantLoanTapeMetadataParams = [];
    intermediateCalcConfigs.forEach((icc) => {
        if (icc.config.type === 'formula') {
            const { loanTapeMetadataProperties, portfolioCovenantQueryParams, } = (0, exports.parseLoanTapeMetadataProperties)(icc.config.formula);
            loanTapeMetadataProperties.forEach((property) => {
                consumedProperties.add(property);
            });
            portfolioCovenantQueryParams.forEach((param) => {
                portfolioCovenantLoanTapeMetadataParams.push(param);
            });
        }
    });
    return {
        loanTapeMetadataProperties: Array.from(consumedProperties),
        portfolioCovenantLoanTapeMetadataParams,
    };
};
exports.getConsumedLoanTapeMetadataProperties = getConsumedLoanTapeMetadataProperties;
//# sourceMappingURL=parseLoanTapeMetadata.js.map